import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { prop, uniqBy } from 'ramda'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE } from './constants'
import {
  TStagedInvoicesResponse,
  TCreateOnceOffInvoiceMeta,
  TCreateOnceOffInvoicePayload,
  TDraftInvoicesPayload,
  TDraftInvoicesSuccessPayload,
  TFetchInvoicesRequest,
  TInvoice,
  TInvoiceIdParam,
  TInvoiceIdResult,
  TInvoiceSendResult,
  TInvoiceState,
  TInvoiceTypeSummary,
  TInvoiceNotification,
  TSaveBulkInvoicesPayload,
  TUpdateInvoiceRequestPayload,
} from './types'

const initialState: TInvoiceState = {
  invoices: [],
  invoiceTypes: [],
  notifications: [],
  draftInvoicesById: {},
}

const invoiceApiSlice = createSlice({
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    invoices_request: (state, { payload }: PayloadAction<TFetchInvoicesRequest>) => {},
    invoices_success: (state, { payload }: PayloadAction<TInvoice[]>) => {
      state.invoices = payload
    },
    invoice_request: (state, { payload }: PayloadAction<TInvoiceIdParam>) => {},
    invoice_success: (state, { payload }: PayloadAction<TInvoice>) => {
      state.invoices = uniqBy(prop('id'), [...state.invoices, payload])
    },
    invoiceUpdate_request: (state, { payload }: PayloadAction<TUpdateInvoiceRequestPayload>) => {},
    invoiceUpdate_success: (state, { payload }: PayloadAction<TInvoice>) => {
      state.invoices = state.invoices.map(inv => (inv.id === payload.id ? payload : inv))
    },
    createOnceOffInvoice_request: {
      reducer: (state, { payload }: PayloadAction<TCreateOnceOffInvoicePayload>) => {},
      prepare: (payload: TCreateOnceOffInvoicePayload, meta: TCreateOnceOffInvoiceMeta) => ({
        payload,
        meta,
        error: null,
      }),
    },
    createOnceOffInvoice_success: {
      reducer: (state, { payload }: PayloadAction<TInvoice, string, TCreateOnceOffInvoiceMeta>) => {
        state.invoices.push(payload)
      },
      prepare: (payload: TInvoice, meta: TCreateOnceOffInvoiceMeta) => ({ payload, meta, error: null }),
    },
    invoiceTypes_request: () => {},
    invoiceTypes_success: (state, { payload }: PayloadAction<TInvoiceTypeSummary[]>) => {
      state.invoiceTypes = payload
    },
    sendInvoices_request: (state, { payload }: PayloadAction<string[]>) => {},
    /**
     * Drop every invoice the send actually settled, keeping the ones that came back
     * unsent for a reason the user still has to deal with. "Has already been sent" is
     * treated as settled, not as a failure.
     */
    sendInvoices_success: (state, { payload }: PayloadAction<TInvoiceSendResult[]>) => {
      state.invoices = state.invoices.filter(inv => {
        const invoiceResult = payload.find(({ id }) => inv.id === id)
        return !invoiceResult || (!invoiceResult.sent && !invoiceResult.result.includes('has already been sent'))
      })
    },
    deleteInvoices_request: (state, { payload }: PayloadAction<string[]>) => {},
    deleteInvoices_success: (state, { payload }: PayloadAction<TInvoiceIdResult[]>) => {
      const ids = payload.map(({ id }) => id)
      state.invoices = state.invoices.filter(inv => !ids.includes(inv.id))
    },
    restoreInvoices_request: (state, { payload }: PayloadAction<string[]>) => {},
    restoreInvoices_success: (state, { payload }: PayloadAction<TInvoiceIdResult[]>) => {
      payload.forEach(({ id }) => {
        const invoice = state.invoices.find(inv => inv.id === id)
        if (!invoice) return
        invoice.tags = {
          ...invoice.tags,
          lightStatus: 'Green' /** @todo check if amount is set. The API doesn't return the actual invoice :| */,
          status: 'Draft',
        }
      })
    },
    notifications_request: () => {},
    notifications_success: (state, { payload }: PayloadAction<TInvoiceNotification[]>) => {
      state.notifications = payload
    },
    draftInvoices_request: (state, { payload }: PayloadAction<TDraftInvoicesPayload>) => {},
    draftInvoices_success: (state, { payload }: PayloadAction<TDraftInvoicesSuccessPayload>) => {
      state.draftInvoicesById[payload.id] = payload.draftInvoices
    },
    /**
     * Never dispatched: `bulkImportInvoices`' own `bulkCreateInvoices` epic posts to
     * `BULK_INVOICES` directly. The request creator is still live as an identifier —
     * `catchRestError` is handed it as the init action, and `notificationCenter` keys an
     * error notification off its type — and the success event is dispatched from there.
     */
    saveBulkInvoices_request: (state, { payload }: PayloadAction<TSaveBulkInvoicesPayload>) => {},
    saveBulkInvoices_success: () => {},
    fetchBulkInvoices_request: () => {},
    fetchBulkInvoices_success: (state, { payload }: PayloadAction<TStagedInvoicesResponse>) => {},
    deleteBulkInvoices_request: () => {},
    deleteBulkInvoices_success: (state, { payload }: PayloadAction<unknown>) => {},
  },
})

const { reducer, actions: events } = invoiceApiSlice

export type InvoiceEvent = ActionType<typeof events>

export { events, initialState, reducer }
